const { getApiUrl } = require('../config/serverEndpoint');
const disabledKey = username => `kelimoPushDisabled:${username}`;
const supported = () => typeof window !== 'undefined' && !window.Capacitor?.isNativePlatform?.() && window.isSecureContext &&
  'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
let activeBinding = null, lifecycle = 0;
function ownsBrowserSession(token) {
  try { const saved = JSON.parse(localStorage.getItem('kelimoSessionV194') || 'null'); return !saved || saved.token === token; }
  catch (_) { return true; }
}
async function sessionBinding(token) {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(token));
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
}
async function worker() {
  const registration = await navigator.serviceWorker.register('/kelimo-push-sw.js', { scope: '/', updateViaCache: 'none' });
  await navigator.serviceWorker.ready;
  return registration;
}
async function bind(registration, binding) {
  const target = registration.active;
  if (!target) throw new Error('Bildirim hizmeti henüz hazır değil. Yeniden dene.');
  await new Promise((resolve, reject) => {
    const channel = new MessageChannel();
    const timeout = setTimeout(() => reject(new Error('Bildirim bağlantısı zaman aşımına uğradı.')), 5000);
    channel.port1.onmessage = event => { clearTimeout(timeout); channel.port1.close(); event.data?.ok ? resolve() : reject(new Error('Bildirim bağlantısı kurulamadı.')); };
    target.postMessage({ type: 'bind', binding }, [channel.port2]);
  });
}
async function config(username, token) {
  const response = await fetch(getApiUrl(`/web-push-config?username=${encodeURIComponent(username)}`), { headers: { Authorization: `Bearer ${token}` }, cache: 'no-store' });
  if (!response.ok) throw new Error('Bildirim durumu alınamadı.');
  return response.json();
}
async function register(username, token, registration, subscription, generation = lifecycle) {
  if (generation !== lifecycle || !ownsBrowserSession(token)) return;
  const response = await fetch(getApiUrl('/web-push-device'), { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, subscription: subscription.toJSON() }) });
  if (!response.ok) throw new Error('Bildirim kaydı tamamlanamadı.');
  const binding = await sessionBinding(token);
  if (generation !== lifecycle) return;
  await bind(registration, binding);
  if (generation === lifecycle) activeBinding = binding;
}
function decodePublicKey(value) {
  const raw = atob(value.replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, character => character.charCodeAt(0));
}
async function pushPreferences(username, token) {
  const remote = await config(username, token);
  return remote.preferences || { turn: true, invite: true, friend: true };
}
async function savePushPreferences(username, token, preferences) {
  const response = await fetch(getApiUrl('/web-push-preferences'), { method: 'PATCH',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ username, preferences }) });
  if (!response.ok) throw new Error('Bildirim tercihleri kaydedilemedi. Yeniden dene.');
  return (await response.json()).preferences;
}
async function pushStatus(username, token) {
  const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const standalone = window.matchMedia('(display-mode: standalone)').matches || navigator.standalone === true;
  if (ios && !standalone) return { available: false, permission: 'Ana ekrandan aç', text: 'Önce ana ekrana ekle, sonra Kelimo simgesinden aç.' };
  if (!supported()) return { available: false, permission: 'Desteklenmiyor', text: 'Bu tarayıcıda web bildirimleri kullanılamıyor.' };
  const remote = await config(username, token);
  const registration = await worker();
  const subscription = await registration.pushManager.getSubscription();
  const enabled = Boolean(subscription && remote.subscribed && Notification.permission === 'granted' && localStorage.getItem(disabledKey(username)) !== '1');
  return { available: remote.enabled, enabled, permissionState: Notification.permission, permission: Notification.permission === 'granted' ? 'İzin verildi' : Notification.permission === 'denied' ? 'İzin engellendi' : 'İzin istenmedi',
    text: !remote.enabled ? 'Sunucu bildirimleri henüz etkin değil.' : enabled ? 'Bildirimler bu cihazda etkin.' : 'Bu cihazda bildirimler kapalı.' };
}
async function enableWebPush(username, token) {
  if (!supported()) throw new Error('Bildirim desteği yok. iPhone’da ana ekran simgesinden aç.');
  // Permission call happens before any await: a direct button gesture on iOS.
  const permissionPromise = Notification.permission === 'default' ? Notification.requestPermission() : Promise.resolve(Notification.permission);
  if (await permissionPromise !== 'granted') throw new Error('Bildirim izni verilmedi. Tarayıcı/telefon ayarlarından izin ver.');
  const remote = await config(username, token);
  if (!remote.enabled || !remote.publicKey) throw new Error('Sunucu bildirimleri etkin değil.');
  const registration = await worker();
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: decodePublicKey(remote.publicKey) });
  await register(username, token, registration, subscription);
  localStorage.removeItem(disabledKey(username));
}
async function disconnectWebPush(username, token) {
  if (!supported() || (token && !ownsBrowserSession(token))) return;
  const generation = ++lifecycle; activeBinding = null;
  const registration = await worker();
  if (generation !== lifecycle) return;
  await bind(registration, null);
  if (generation !== lifecycle) return;
  const subscription = await registration.pushManager.getSubscription();
  if (generation !== lifecycle) return;
  if (subscription) await subscription.unsubscribe();
  if (username && token) {
    const response = await fetch(getApiUrl(`/web-push-device?username=${encodeURIComponent(username)}`), { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    if (!response.ok && response.status !== 401) throw new Error('Bildirim kaydı kaldırılamadı. Yeniden dene.');
  }
}
async function disableWebPush(username, token) {
  localStorage.setItem(disabledKey(username), '1');
  await disconnectWebPush(username, token);
}
function validDestination(value) {
  return value && ['turn', 'invite', 'friend', 'time', 'result'].includes(value.kind) && typeof value.gameId === 'string' && value.gameId.length > 0 && value.gameId.length < 150 &&
    typeof value.binding === 'string' && /^[a-f0-9]{64}$/.test(value.binding);
}
function takePushDestination() {
  const params = new URLSearchParams(window.location.search);
  if (!params.has('kelimoPush')) return null;
  const destination = { kind: params.get('kelimoPush'), gameId: params.get('target'), binding: params.get('binding') };
  const url = new URL(window.location.href); for (const key of ['kelimoPush', 'target', 'binding']) url.searchParams.delete(key);
  window.history.replaceState(window.history.state, '', url);
  return validDestination(destination) && destination.binding === activeBinding ? destination : null;
}
async function connectWebPush(username, token, onOpen) {
  if (!supported() || !ownsBrowserSession(token)) return () => {};
  const generation = ++lifecycle;
  const binding = await sessionBinding(token);
  if (generation !== lifecycle) return () => {};
  activeBinding = binding;
  const listener = event => {
    if (event.data?.type === 'push-open' && validDestination(event.data) && event.data.binding === binding && activeBinding === binding && ownsBrowserSession(token)) onOpen(event.data);
  };
  navigator.serviceWorker.addEventListener('message', listener);
  const stop = () => navigator.serviceWorker.removeEventListener('message', listener);
  const pending = takePushDestination(); if (pending && localStorage.getItem(disabledKey(username)) !== '1' && Notification.permission !== 'denied') onOpen(pending);
  // Opening a verified destination must not depend on renewing the device subscription.
  try {
  const registration = await worker();
  if (generation !== lifecycle) { stop(); return () => {}; }
  await bind(registration, binding);
  const subscription = await registration.pushManager.getSubscription();
  if (localStorage.getItem(disabledKey(username)) === '1' || Notification.permission === 'denied') {
    await bind(registration, null); activeBinding = null; stop();
    if (subscription) await subscription.unsubscribe();
    return () => {};
  }
  if (subscription && Notification.permission === 'granted' && localStorage.getItem(disabledKey(username)) !== '1') {
    await register(username, token, registration, subscription, generation);
  } else if (generation === lifecycle) await bind(registration, binding);
  if (generation !== lifecycle) { stop(); return () => {}; }
  } catch (_) { /* Destination navigation survives a temporary registration failure. */ }
  if (generation !== lifecycle || activeBinding !== binding) { stop(); return () => {}; }
  return stop;
}
function postView(view) {
  if (!activeBinding || !supported()) return;
  navigator.serviceWorker.getRegistration().then(registration => registration?.active?.postMessage(Object.assign({ type: 'view', binding: activeBinding }, view))).catch(() => {});
}
module.exports = { postView, pushPreferences, savePushPreferences, pushStatus, enableWebPush, disableWebPush, disconnectWebPush, connectWebPush, sessionBinding, validDestination, takePushDestination };
